import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import authApiService from "../../../services/authServices";
import Modal from "../../../utils/modalUtils";
import { sortCountriesForDropdown } from "../../../utils/sortCountriesForDropdown";

const UpdateCity = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const cityId = location.state?.cityId || "";
    const [countryTimezones, setCountryTimezones] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [statesForCountry, setStatesForCountry] = useState([]);
    const [isCustomState, setIsCustomState] = useState(false);

    const [cityForUpdate, setCityForUpdate] = useState({
        id: "",
        name: "",
        country: "",
        state: "",
        timezone: "",
        inactive_msg: "",
        countdown_msg: "",
        active: false,
    });
    // const cityId = localStorage.getItem("cityId")

    useEffect(() => {
        const fetchCityData = async () => {
            try {

                const response = await authApiService.get("cityForUpdate", { "id": cityId });
                const data = response.data || [];
                setCityForUpdate(data);

                if (data.length > 0) {
                    localStorage.setItem("cityId", data[0].id);
                }
            } catch (error) {
                console.error("Error fetching city details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCityData();
    }, []);

    useEffect(() => {
        const fetchCountryTimeZones = async () => {
            try {
                const response = await authApiService.get("getCountryTimeZones");
                if (response && Array.isArray(response.data)) {
                    const rows = response.data;
                    setCountryTimezones(rows);
                    const uniqueCountriesMap = new Map();
                    rows.forEach((row) => {
                        const key = row.countryCode || row.countryName;
                        if (!uniqueCountriesMap.has(key)) {
                            uniqueCountriesMap.set(key, {
                                countryCode: row.countryCode,
                                countryName: row.countryName,
                            });
                        }
                    });
                    setCountryOptions(
                        sortCountriesForDropdown(Array.from(uniqueCountriesMap.values()))
                    );
                } else {
                    setCountryTimezones([]);
                    setCountryOptions([]);
                    setTimezoneOptions([]);
                }
            } catch (error) {
                console.error("Error fetching country timezones", error);
                setCountryTimezones([]);
                setCountryOptions([]);
                setTimezoneOptions([]);
            }
        };

        fetchCountryTimeZones();
    }, []);

    useEffect(() => {
        if (!cityForUpdate.country) {
            setTimezoneOptions([]);
            return;
        }
        const filtered = countryTimezones.filter(
            (row) => row.countryName === cityForUpdate.country
        );
        const tzOptions = filtered.map((row) => ({
            value: row.timeZone,
            label: `${row.timeZone} (${row.gmtOffset})`,
        }));
        setTimezoneOptions(tzOptions);
    }, [cityForUpdate.country, countryTimezones]);

    useEffect(() => {
        const fetchStates = async () => {
            if (!cityForUpdate.country) {
                setStatesForCountry([]);
                return;
            }
            const selected = countryOptions.find((c) => c.countryName === cityForUpdate.country);
            const countryCode = selected?.countryCode;
            if (!countryCode) return;

            try {
                const response = await authApiService.get("getStatesByCountry", {
                    countryCode,
                });
                if (response && Array.isArray(response.data) && response.data.length > 0) {
                    setStatesForCountry(response.data);
                    setIsCustomState(false);
                } else {
                    setStatesForCountry([]);
                }
            } catch (error) {
                console.error("Error fetching states by country:", error);
                setStatesForCountry([]);
            }
        };

        fetchStates();
    }, [cityForUpdate.country, countryOptions]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCityForUpdate((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;
        setCityForUpdate((prev) => ({
            ...prev,
            country: value,
            state: "",
            timezone: "",
        }));
        setStatesForCountry([]);
        setIsCustomState(false);
    };

    const handleStateChange = (e) => {
        const { value } = e.target;
        if (value === "__CUSTOM__") {
            setIsCustomState(true);
            setCityForUpdate((prev) => ({ ...prev, state: "" }));
        } else {
            setIsCustomState(false);
            handleChange(e);
        }
    };


    /**
     * Handles city update submission
     * @param {Event} e - The form submit event
     */
    const handleUpdateCity = async (e) => {
        e.preventDefault();
        try {
            const response = await authApiService.put(`updateCity?id=${cityId}`, {
                name: cityForUpdate.name,
                country: cityForUpdate.country,
                state: cityForUpdate.state,
                timezone: cityForUpdate.timezone,
                inactive_msg: cityForUpdate.inactive_msg,
                countdown_msg: cityForUpdate.countdown_msg,
                active: cityForUpdate.active
            });
            console.log("City updated successfully:", response.data);
            Modal.fire({
                icon: "success",
                title: "Success",
                text: "City Updated successfully!",
                confirmButtonText: "OK",
                confirmButtonColor: "#addd1e"
            }).then(() => {
                navigate("/carter/citymanage");
            });
        } catch (error) {
            console.error("Error updating city:", error);
            Modal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to update city.",
                confirmButtonText: "OK",
                confirmButtonColor: "#addd1e"
            });
        }
    };

    /**
     * Handles city deletion with confirmation
     */
    const handleDeleteCity = async () => {
        try {
            if (!cityForUpdate.id) {
                Modal.fire({
                    icon: "error",
                    title: "Error",
                    text: "City ID is missing!",
                    confirmButtonText: "OK",
                    confirmButtonColor: "#addd1e"
                });
                return;
            }

            const confirmDelete = await Modal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#addd1e",
                cancelButtonColor: "#b38ec1",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel"
            });

            if (confirmDelete.isConfirmed) {
                try {
                    const response = await authApiService.delete(`deleteCity?id=${cityId}`);
                    console.log("City deleted successfully:", response.data);
                    Modal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: "City has been deleted.",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#addd1e"
                    }).then(() => {
                        navigate("/carter/citymanage");
                        // Clear city data after deletion
                        setCityForUpdate({
                            id: "",
                            name: "",
                            country: "",
                            state: "",
                            timezone: "",
                            inactive_msg: "",
                            countdown_date: "",
                            active: false,
                        });
                        // Remove cityId from localStorage
                        localStorage.removeItem("cityId");
                    });
                } catch (error) {
                    console.error("Error deleting city:", error);
                    Modal.fire({
                        icon: "error",
                        title: "Error",
                        text: error.response?.data?.message || "Failed to delete city.",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#addd1e"
                    });
                }
            }
        } catch (error) {
            console.error("Error in handleDeleteCity:", error);
        }
    };


    return (
        <div className="page-container col-sm-9">
            <div id="registerCity">
                <form
                    id="registrationCity"
                    onSubmit={handleUpdateCity}
                >
                    <input id="id" name="id" type="hidden" value={cityForUpdate?.id || ""} />
                    <input id="version" name="version" type="hidden" value={cityForUpdate?.version || ""} />
                    {/* <input
                        id="countDownDate"
                        name="countDownDate"
                        type="hidden"
                        value={cityForUpdate?.countdown_date || ""}
                    /> */}
                    <div className="row">
                        <div className="col-sm-4">
                            <table cellPadding={3} border={0} className="shadow-sm grey-box">
                                <tbody>
                                    <tr>
                                        <td>
                                            <label htmlFor="name" className="desc">
                                                City <span className="req">*</span>
                                            </label>
                                        </td>
                                        <td>
                                            <input
                                                id="name"
                                                name="name"
                                                className="text medium"
                                                type="text"
                                                value={cityForUpdate?.name || ""}
                                                onChange={handleChange}
                                                required
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="country" className="desc">
                                                Country <span className="req">*</span>
                                            </label>
                                        </td>
                                        <td>
                                            <select
                                                id="country"
                                                name="country"
                                                className="text medium select_field"
                                                value={cityForUpdate?.country || ""}
                                                onChange={handleCountryChange}
                                                required
                                            >
                                                <option value="">Select Country</option>
                                                {countryOptions.map((country) => (
                                                    <option
                                                        key={country.countryCode || country.countryName}
                                                        value={country.countryName}
                                                    >
                                                        {country.countryName}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="state" className="desc">
                                                State <span className="req">*</span>
                                            </label>
                                        </td>
                                        <td>
                                            {statesForCountry.length > 0 ? (
                                                <>
                                                    <select
                                                        id="state"
                                                        name="state"
                                                        className="text medium select_field"
                                                        value={isCustomState ? "__CUSTOM__" : cityForUpdate?.state || ""}
                                                        onChange={handleStateChange}
                                                        required={!isCustomState}
                                                    >
                                                        <option value="">Please Select</option>
                                                        {statesForCountry.map((state) => (
                                                            <option key={state.id} value={state.name}>
                                                                {state.name}
                                                            </option>
                                                        ))}
                                                        <option value="__CUSTOM__">Other / Add new state</option>
                                                    </select>
                                                    {isCustomState && (
                                                        <input
                                                            id="customState"
                                                            name="state"
                                                            className="text medium"
                                                            type="text"
                                                            placeholder="Enter State"
                                                            value={cityForUpdate?.state || ""}
                                                            onChange={handleChange}
                                                            maxLength={50}
                                                            required
                                                            style={{ marginTop: "8px", display: "block" }}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <input
                                                    id="state"
                                                    name="state"
                                                    className="text medium"
                                                    type="text"
                                                    placeholder={cityForUpdate?.country ? "Enter State" : "Select country first"}
                                                    value={cityForUpdate?.state || ""}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="timezone" className="desc">
                                                International Clock <span className="req">*</span>
                                            </label>
                                        </td>
                                        <td>
                                            <select
                                                id="timezone"
                                                name="timezone"
                                                className="text medium select_field"
                                                value={cityForUpdate?.timezone || ""}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select time zone</option>
                                                {timezoneOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="inactiveMessage" className="desc">
                                                Inactive Message
                                            </label>
                                        </td>
                                        <td>
                                            <input
                                                id="inactiveMessage"
                                                name="inactive_msg"
                                                className="text medium"
                                                type="text"
                                                value={cityForUpdate?.inactive_msg || ""}
                                                onChange={handleChange}
                                                maxLength={50}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="countDownMessage" className="desc">
                                                CountDown Message
                                            </label>
                                        </td>
                                        <td>
                                            <input
                                                name="countdown_msg"
                                                className="text medium"
                                                type="text"
                                                value={cityForUpdate?.countdown_msg || ""}
                                                onChange={handleChange}
                                                maxLength={50}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label htmlFor="active" className="desc">
                                                Active
                                            </label>
                                        </td>
                                        <td>
                                            <input id="active" name="active" type="checkbox" checked={cityForUpdate?.active} onChange={handleChange} />
                                            <input type="hidden" value={cityForUpdate?.active ? 1 : 0} name="_active" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2} style={{ padding: "5px 10px" }}>
                                                <button type="submit" className="green_btn">
                                                    <span>Save</span>
                                                </button>
                                                <Link to="/carter/citymanage" className="gray_btn mx-2">
                                                    <span>Cancel</span>
                                                </Link>
                                                <button type="button" className="gray_dark_btn" onClick={handleDeleteCity}>
                                                    <span>Delete</span>
                                                </button>
                                            <div className="clear" />
                                            <p className="mandatory">
                                                <span className="asterisk">*</span> Mandatory Field
                                            </p>
                                            <input
                                                id="bCancel"
                                                type="hidden"
                                                name="bCancel"
                                                defaultValue="false"
                                            />
                                            <input
                                                id="bDelete"
                                                type="hidden"
                                                name="bDelete"
                                                defaultValue="false"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </form>
            </div>
        </div>

    )
}

export default UpdateCity